import { useRef } from "react";
import { useIntl } from "react-intl";
import { WmChart, WmChartSlice } from "@watermarkinsights/ripple-react";
import { AiDataCaptureCaption } from "../AiDataCaptureCaption";
import { DownloadActionMenu } from "./DownloadActionMenu";
import { useExportToPng } from "../export-conversation/useExportToPng";
import { buildPieChartCsvData, downloadTableAsCsv } from "../ai-chat-message-utils";
import type { PieChartPayload } from "../ai-chat-message-utils";

interface AiPieChartProps {
  payload: PieChartPayload;
  readOnly?: boolean;
}

export const AiPieChart = ({ payload, readOnly = false }: AiPieChartProps) => {
  const intl = useIntl();
  const { label, categoryAxisLabel, valueLabel, valueFormat, data } = payload;
  const elementRef = useRef<HTMLDivElement>(null);
  const { isExporting, handleExport } = useExportToPng({
    elementRef,
    fileTitle: label || "Currents_Conversation_Chart",
    errorMessage: intl.formatMessage({ id: "app.toolboxChat.action.downloadPngError" }),
  });

  // Stable key ensures WmChart remounts with all slices present when data changes
  const stableChartKey = data.map((d) => `${d.legend}:${d.amount}`).join("|");

  const handleDownloadCsv = () => {
    const { columns, rows } = buildPieChartCsvData(data, categoryAxisLabel, valueLabel);
    downloadTableAsCsv(columns, rows, label);
  };

  return (
    <div ref={elementRef}>
      <div className="tcw-chart-card">
        <div className="tcw-chart-header">
          {label && <p className="tcw-chart-label">{label}</p>}
          {!readOnly && (
            <DownloadActionMenu
              tooltip={intl.formatMessage({ id: "app.toolboxChat.action.downloadChart" })}
              onDownloadPng={handleExport}
              onDownloadCsv={handleDownloadCsv}
              disabled={isExporting}
            />
          )}
        </div>
        <div className="tcw-chart-wrapper">
          <WmChart
            key={stableChartKey}
            chartType="doughnut3"
            label={label ?? "Pie Chart "}
            labelPosition="none"
            valueFormat={valueFormat ?? "percentage"}
          >
            {data.map((item, index) => (
              <WmChartSlice
                key={index}
                amount={String(item.amount)}
                legend={item.legend}
              />
            ))}
          </WmChart>
        </div>
        <AiDataCaptureCaption />
      </div>
    </div>
  );
};
